import {
  settings,
  routerHelpers,
  dataService
} from "@thegetty-private/rad-app-utils";
import internal from "@/data/internal-ontologies";
import { getApiPath } from "@/helpers/configHelper";
import * as localLinkedArtHelpers from "@/helpers/LinkedArt";

// maps LOD object types to the paths of that type of object in the app
const typeToPath = {
  HumanMadeObject: "object",
  Group: "group",
  PropositionalObject: "exhibition",
  Person: "person",
  Place: "gallery"
};

/**
 * Handles the TMS Redirect for Actors and Objects
 *
 * @param {object} to - the VueRouter To
 * @param {object} _from - the VueRouter From (not used)
 * @param {function} next - the VueRouter next() function
 *
 */
export function checkForAndHandleTmsDorRedirect(to, _from, next) {
  // if "slug" is actually a TMS or DOR id, handle the reroute
  if (isTmsOrDorId(to)) {
    let idType = "HumanMadeObject";
    if (to.name == "group" || to.name == "person") {
      idType = "Actor";
    }

    handleTmsAndDorReroute(to, next, idType);
    return;
  }

  next();
}

/**
 * This will be removed with DEV-8661
 * Note: This will be removed with DEV-8661 when we get the actual "slug" in new LOD
 *
 * Used in getIndexedIdFromRoute to construct the tms id for idManager lookup
 */
export function constructFullTmsId(routeObj) {
  const routeName = routeObj.name;
  const tmsId = routeObj.params.slug;

  let tmsType;
  if (routeName == "object") {
    tmsType = "object";
  } else if (routeName == "gallery") {
    tmsType = "gallery";
  } else if (routeName == "exhibition") {
    tmsType = "exhibition";
  } else {
    tmsType = "constituent";
  }

  return "urn:getty-local:tms:" + tmsType + ":id/" + tmsId;
}

/**
 * Note: This will be updated with DEV-8661 when we get the actual "slug" in new LOD
 *
 * Takes the route information and gets the indexedId for the current object (HumanMadeObject/Actor/etc)
 *
 * @param {object} routeObject - the object representing the route we're at
 *
 * @example this.$route in a Vue component
 *
 * @return {object} an object that can have one or more of the following fields:
 * @example {
 *            indexedId: {string},
 *            status: {string},
 *            exception: {object}
 *          }
 */
export async function getIndexedIdFromRoute(routeObj) {
  return routerHelpers.getIndexedIdFromRoute(
    routeObj,
    settings.getBaseUrlForIdManager(),
    internal.ID_MANAGER_TARGET_GENERATOR,
    undefined,
    undefined
  );
}

/**
 * Takes an indexed elasticsearch object and constructs the router "to" property to navigate to the corresponding Vue page
 * @param {object} esObject - the indexed elasticsearch object as returned from our python endpoints
 *
 * @return {object} the router "to" object
 * @example {
 *            name: {string} "name" of route in router.js
 *            params: {
 *              slug: {string} "path" of route in router.js
 *            }
 *            state: {
 *              indexedId: {string} "object/uuid",
 *              slug: {string} "path" of route in router.js
 *            }
 *          }
 */
export function getRouterToFromIndexedObject(esObject) {
  const indexedId = esObject.id;
  if (indexedId == undefined) {
    return;
  }
  const routeName = getRouteNameFromIndexedId(indexedId);
  const slugFromIdManager = esObject.id_manager_slug;

  const route = {
    name: routeName,
    params: {
      slug: slugFromIdManager
    },
    state: {
      indexedId: indexedId,
      slug: slugFromIdManager
    }
  };

  return route;
}

/**
 * Takes an indexedId and returns the route "name" used in router.js
 * Note: this may change with DEV-8597
 *
 * @param {string} indexedId - the id in the form of "type/uuid"
 * @return {string} the route "name" used in router.js
 *
 */
export function getRouteNameFromIndexedId(indexedId) {
  let routeName = indexedId.split("/")[0];

  return routeName;
}

/**
 * This is a work in progress (DEV-6435) and will be updated/documented later
 * Re-examine the need for this in DEV-8704
 */
export function getSlugWithPath(object) {
  const slug = localLinkedArtHelpers.getSlugID(object);

  if (slug == undefined) {
    return;
  }
  // get the path from the object's type
  let path = typeToPath[object.type];

  return "/" + path + "/" + slug;
}

/**
 * Handles url routing if we get passed a TMS or DOR id as the slug
 *
 * @param {object} to - the target Route Object being navigated to
 * @param {function} next - Next function called to resolve the hook
 * @param {string} idType - the type of to look up
 *
 */
export async function handleTmsAndDorReroute(to, next, idType) {
  let routeSlug = to.params.slug;

  // start constructing the request url starting with the base url
  let url = getApiPath("/") + "api/redirect?lodType=" + idType;

  // construct full request url for redirect endpoint
  if (routeSlug.startsWith("tms:")) {
    const tmsId = routeSlug.replace("tms:", "");
    url = url + "&id=" + tmsId + "&type=tms";
  } else if (routeSlug.startsWith("dor:")) {
    const dorId = routeSlug.replace("dor:", "");
    url = url + "&id=" + dorId + "&type=dor";
  }

  // get object data from /redirect endpoint
  const results = await dataService.axiosGet(url);

  // construct route "next"
  const indexedId = results.data.id;
  const routeName = getRouteNameFromIndexedId(indexedId);
  const slug = routerHelpers.getSlugFromSlugWithPath(
    results.data.slug_with_path
  );

  const routeNext = {
    name: routeName,
    params: {
      slug: slug
    },
    state: {
      indexedId: indexedId,
      slug: slug
    }
  };

  next(routeNext);
}

/**
 * Checks if the route's "slug" param is a TMS or DOR id
 *
 * @param {object} to - the target Route Object being navigated to
 * @return {boolean} True if the slug is a TMS or DOR id, false if not
 *
 */
export function isTmsOrDorId(to) {
  const slug = to.params.slug;
  if (slug.startsWith("tms:") || slug.startsWith("dor:")) {
    return true;
  }
  return false;
}
